import React, { useState, useCallback } from 'react';
import { useEventTranslation } from '../../translation/hooks/useEventTranslation';
import TranslateButton from '../TranslateButton';
import type { TranslatableEvent } from '../../types/translation';

interface EventTranslationProviderProps {
  /** Children components that will receive translated events */
  children: (props: {
    displayEvents: TranslatableEvent[];
    isTranslating: boolean;
    isTranslatedView: boolean;
    onEventsFetched: (events: TranslatableEvent[]) => void;
    eventsOverride?: TranslatableEvent[];
    translateButton: React.ReactNode;
  }) => React.ReactNode;
}

/**
 * EventTranslationProvider - Self-contained translation wrapper for event sections
 * 
 * Handles all translation logic internally and provides a clean API for child components.
 * Automatically manages state, caching, and toggle functionality.
 * 
 * @example
 * ```tsx
 * <EventTranslationProvider>
 *   {({ onEventsFetched, eventsOverride, translateButton }) => (
 *     <FeaturedEventsSection
 *       beforeContent={
 *         <div className="flex justify-between">
 *           <h2>Events</h2>
 *           {translateButton}
 *         </div>
 *       }
 *       onEventsFetched={onEventsFetched}
 *       eventsOverride={eventsOverride}
 *     />
 *   )}
 * </EventTranslationProvider>
 * ```
 */
export const EventTranslationProvider: React.FC<EventTranslationProviderProps> = ({
  children,
}) => {
  const apiKey = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY;
  const [originalEvents, setOriginalEvents] = useState<TranslatableEvent[]>([]);
  const [displayEvents, setDisplayEvents] = useState<TranslatableEvent[]>([]);

  const { 
    isTranslating, 
    isTranslatedView,
    toggleTranslation 
  } = useEventTranslation({ apiKey });

  const handleEventsFetched = useCallback((events: TranslatableEvent[]) => {
    setOriginalEvents(events);
    setDisplayEvents(events);
  }, []);

  const handleTranslationToggle = useCallback(() => {
    if (originalEvents.length > 0) {
      toggleTranslation(originalEvents, setDisplayEvents);
    }
  }, [originalEvents, toggleTranslation]);

  return (
    <>
      {children({
        displayEvents,
        isTranslating,
        isTranslatedView,
        onEventsFetched: handleEventsFetched,
        eventsOverride: displayEvents.length > 0 ? displayEvents : undefined,
        translateButton: apiKey ? (
          <TranslateButton
            onClick={handleTranslationToggle}
            isTranslating={isTranslating}
            isTranslated={isTranslatedView}
          />
        ) : null,
      })}
    </>
  );
};

export default EventTranslationProvider;
